import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock,
  Gamepad2, CreditCard, Zap, Loader2, Layers, Wallet, TestTube2,
} from 'lucide-react';

/**
 * Provider Health — unified dashboard for every upstream integration.
 *
 * Two sections:
 *   1. Game Providers — sourced from /api/admin/provider-balance/ (cached);
 *      Test button calls /api/admin/provider-balance/{code}/test-connection
 *      which hits the upstream /health/{code} endpoint (HMAC-signed).
 *   2. Payment Gateways — sourced from /api/admin/payment-gateways;
 *      Test button calls /api/admin/payment-gateways/{id}/test-connection.
 *
 * Every item supports an individual Test action; the header lets operators
 * batch-test per section or everything at once.
 */
export default function ProviderHealthPage() {
  const [games, setGames] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-row state: {key, testing, result}
  const [rowState, setRowState] = useState({});
  // Bulk button state
  const [bulkGame, setBulkGame] = useState(false);
  const [bulkPay, setBulkPay] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [gr, pr] = await Promise.all([
        api.get('/api/admin/provider-balance/'),
        api.get('/api/admin/payment-gateways'),
      ]);
      const gItems = gr.data?.items || [];
      setGames(gItems.map((g) => ({
        key: `game:${g.provider_code}`,
        type: 'game',
        code: g.provider_code,
        name: g.provider_name,
        logo: g.logo_url,
        ok: g.ok === true || g.ok === undefined ? (g.ok === true) : false,
        last_checked_at: g.last_checked_at,
        status: g.ok ? 'healthy' : (g.last_checked_at ? 'error' : 'unchecked'),
        error: g.error,
      })));
      const pList = Array.isArray(pr.data) ? pr.data : (pr.data?.items || []);
      setGateways(pList.map((p) => ({
        key: `pay:${p.id}`,
        type: 'payment',
        code: p.provider_code,
        display_name: p.display_name || p.provider_code,
        id: p.id,
        status: p.health_status || 'unchecked',
        last_checked_at: p.last_checked_at,
        last_error: p.last_error,
      })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const testGameProvider = async (code) => {
    const key = `game:${code}`;
    setRowState((s) => ({ ...s, [key]: { testing: true } }));
    try {
      const res = await api.post(`/api/admin/provider-balance/${code}/test-connection`);
      setRowState((s) => ({ ...s, [key]: { testing: false, result: res.data } }));
      setGames((rows) => rows.map((r) => r.code === code ? {
        ...r,
        ok: !!res.data.ok,
        status: res.data.ok ? 'healthy' : 'error',
        last_checked_at: res.data.checked_at,
        error: res.data.error,
      } : r));
      if (res.data.ok) toast.success(`${code} · connection OK (${res.data.duration_ms} ms)`);
      else toast.error(`${code} · ${res.data.error || 'connection failed'}`);
      return res.data.ok;
    } catch (e) {
      setRowState((s) => ({ ...s, [key]: { testing: false } }));
      toast.error(e?.response?.data?.detail || 'Test failed');
      return false;
    }
  };

  const testPaymentGateway = async (id, displayName) => {
    const key = `pay:${id}`;
    setRowState((s) => ({ ...s, [key]: { testing: true } }));
    try {
      const res = await api.post(`/api/admin/payment-gateways/${id}/test-connection`);
      const ok = !!res.data.success || res.data.status === 'healthy';
      setRowState((s) => ({ ...s, [key]: { testing: false, result: res.data } }));
      setGateways((rows) => rows.map((r) => r.id === id ? {
        ...r,
        status: res.data.status || (ok ? 'healthy' : 'error'),
        last_checked_at: new Date().toISOString(),
        last_error: res.data.error || null,
      } : r));
      if (ok) toast.success(`${displayName} · ${res.data.message || 'connection OK'}`);
      else toast.error(`${displayName} · ${res.data.error || res.data.message || 'connection failed'}`);
      return ok;
    } catch (e) {
      setRowState((s) => ({ ...s, [key]: { testing: false } }));
      toast.error(e?.response?.data?.detail || 'Test failed');
      return false;
    }
  };

  const testAllGames = async () => {
    setBulkGame(true);
    let ok = 0, failed = 0;
    for (const g of games) {
      // Sequential to avoid rate-limiting upstream
      const success = await testGameProvider(g.code);
      success ? ok++ : failed++;
    }
    setBulkGame(false);
    toast.success(`Game providers tested — ${ok} healthy / ${failed} failed`);
  };

  const testAllPayments = async () => {
    setBulkPay(true);
    let ok = 0, failed = 0;
    for (const p of gateways) {
      const success = await testPaymentGateway(p.id, p.display_name);
      success ? ok++ : failed++;
    }
    setBulkPay(false);
    toast.success(`Payment gateways tested — ${ok} healthy / ${failed} failed`);
  };

  const testEverything = async () => {
    await testAllGames();
    await testAllPayments();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="provider-health-loading">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  const totalCount = games.length + gateways.length;
  const healthyGames = games.filter((g) => g.ok).length;
  const healthyPays = gateways.filter((p) => p.status === 'healthy').length;
  const healthy = healthyGames + healthyPays;
  const issues = totalCount - healthy;

  return (
    <div className="space-y-5" data-testid="provider-health-page">
      {/* Header + bulk buttons */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#D4AF37]" /> Provider Health
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Live status for every upstream integration — game providers and payment gateways. Test
            individually, or run a sweep across an entire section.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={testAllGames}
            disabled={bulkGame || games.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 text-[#a78bfa] font-semibold text-sm hover:bg-[#8b5cf6]/20 disabled:opacity-40 transition"
            data-testid="test-all-games-btn"
          >
            {bulkGame ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gamepad2 className="w-4 h-4" />}
            Test Game Providers
          </button>
          <button
            onClick={testAllPayments}
            disabled={bulkPay || gateways.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#22c55e]/10 border border-[#22c55e]/30 text-green-400 font-semibold text-sm hover:bg-[#22c55e]/20 disabled:opacity-40 transition"
            data-testid="test-all-payments-btn"
          >
            {bulkPay ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Test Payment Gateways
          </button>
          <button
            onClick={testEverything}
            disabled={bulkGame || bulkPay || totalCount === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#D4AF37] text-black font-semibold text-sm hover:bg-[#F5D76E] disabled:opacity-40 transition"
            data-testid="test-everything-btn"
          >
            {(bulkGame || bulkPay) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Test Everything
          </button>
          <button
            onClick={fetchAll}
            className="p-2 rounded-xl bg-[#0B0E14] border border-white/10 text-gray-400 hover:text-white transition"
            data-testid="refresh-health-btn"
            title="Reload cached status"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="health-summary">
        <SummaryTile
          icon={Layers}
          color="#D4AF37"
          label="Total Integrations"
          value={totalCount}
          sub={`${games.length} game · ${gateways.length} payment`}
          testid="summary-total"
        />
        <SummaryTile
          icon={CheckCircle2}
          color="#22c55e"
          label="Healthy"
          value={healthy}
          sub={`${healthyGames} game · ${healthyPays} payment`}
          testid="summary-healthy"
        />
        <SummaryTile
          icon={AlertTriangle}
          color={issues ? '#ef4444' : '#6b7280'}
          label="Issues"
          value={issues}
          sub={issues ? 'need attention' : 'all clear'}
          testid="summary-issues"
        />
        <SummaryTile
          icon={Clock}
          color="#3b82f6"
          label="Last Update"
          value={gamesLastChecked(games, gateways)}
          sub="from cached health"
          small
          testid="summary-updated"
        />
      </div>

      {/* Game providers section */}
      <Section
        title="Game Providers"
        icon={Gamepad2}
        color="#8b5cf6"
        count={games.length}
        testid="section-games"
        empty="No game providers configured yet."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {games.map((g) => (
            <HealthCard
              key={g.key}
              row={g}
              state={rowState[g.key] || {}}
              onTest={() => testGameProvider(g.code)}
              icon={Gamepad2}
              kind="Game Provider"
              testid={`health-game-${g.code}`}
            />
          ))}
        </div>
      </Section>

      {/* Payment gateways section */}
      <Section
        title="Payment Gateways"
        icon={Wallet}
        color="#22c55e"
        count={gateways.length}
        testid="section-payments"
        empty="No payment gateways configured yet."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {gateways.map((p) => (
            <HealthCard
              key={p.key}
              row={{
                key: p.key,
                code: p.code,
                name: p.display_name,
                ok: p.status === 'healthy',
                status: p.status,
                last_checked_at: p.last_checked_at,
                error: p.last_error,
              }}
              state={rowState[p.key] || {}}
              onTest={() => testPaymentGateway(p.id, p.display_name)}
              icon={CreditCard}
              kind="Payment Gateway"
              testid={`health-pay-${p.id}`}
            />
          ))}
        </div>
      </Section>
    </div>
  );
}

function gamesLastChecked(games, gateways) {
  const all = [...games, ...gateways]
    .map((r) => r.last_checked_at)
    .filter(Boolean)
    .sort()
    .reverse();
  if (!all[0]) return '—';
  try { return new Date(all[0]).toLocaleTimeString(); } catch { return '—'; }
}

function SummaryTile({ icon: Icon, color, label, value, sub, small, testid }) {
  return (
    <div
      className="rounded-2xl p-4 bg-[#131822] border border-white/10 flex items-center gap-3"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}18` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <p className={`${small ? 'text-base' : 'text-2xl'} font-bold tabular-nums text-white truncate`}>
          {value}
        </p>
        {sub && <p className="text-[10px] text-gray-500 truncate">{sub}</p>}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, color, count, testid, children, empty }) {
  return (
    <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} /> {title}
          <span className="text-[10px] text-gray-500 font-normal">({count})</span>
        </h3>
      </div>
      {count === 0 ? (
        <p className="text-xs text-gray-500 italic text-center py-6">{empty}</p>
      ) : (
        children
      )}
    </div>
  );
}

function HealthCard({ row, state, onTest, icon: Icon, kind, testid }) {
  const { testing } = state;
  const ok = row.ok;
  const unchecked = !row.last_checked_at && !row.status;
  const isError = !ok && !unchecked;

  const border = isError
    ? 'border-red-500/30 bg-red-500/5'
    : ok
      ? 'border-green-500/30 bg-green-500/5'
      : 'border-white/10 bg-white/[0.02]';
  const StatusIcon = unchecked ? Activity : isError ? XCircle : CheckCircle2;
  const statusColor = unchecked ? '#6b7280' : isError ? '#ef4444' : '#22c55e';
  const statusLabel = unchecked ? 'Unchecked' : isError ? 'Unhealthy' : 'Healthy';

  return (
    <div className={`rounded-xl p-3 border ${border} transition`} data-testid={testid}>
      <div className="flex items-start gap-2 mb-2">
        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate capitalize">
            {row.name || row.code}
          </p>
          <p className="text-[10px] text-gray-500 font-mono">{kind} · {row.code}</p>
        </div>
        <div
          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: `${statusColor}18`, color: statusColor }}
        >
          <StatusIcon className="w-3 h-3" /> {statusLabel}
        </div>
      </div>

      {row.error && (
        <p className="text-[10px] text-red-300 mb-2 leading-tight line-clamp-2" title={row.error}>
          {row.error}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {row.last_checked_at ? new Date(row.last_checked_at).toLocaleString() : 'never checked'}
        </span>
        <button
          onClick={onTest}
          disabled={testing}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#0B0E14] border border-white/10 text-[11px] font-semibold text-[#D4AF37] hover:bg-white/5 disabled:opacity-40 transition"
          data-testid={`${testid}-test-btn`}
        >
          {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <TestTube2 className="w-3 h-3" />}
          {testing ? 'Testing…' : 'Test'}
        </button>
      </div>
    </div>
  );
}
